import { defineStore } from 'pinia';
import { computed, nextTick, ref } from 'vue';

interface ScrollStoreConversationState {
  conversationId: string;
  showNewContentButton: boolean;
  messageContainer: HTMLElement | null;
  bottomOfMessageContainer: HTMLElement | null;
  newContentList: NewContentScrollPosition[];
  scrollPositionAtTop?: boolean;
}

const defaultScrollOptions: ScrollIntoViewOptions = {
  behavior: 'smooth',
  block: 'end',
  inline: 'nearest',
};

export interface NewContentScrollPosition {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

export interface ShowNewContentButtonState {
  showNewContentButton: boolean;
  newContentCount: number;
}

export const useScrollStore = defineStore('scrollStore', () => {
  const scrollState = ref<ScrollStoreConversationState[]>([]);

  const getConversationById = (conversationId: string): ScrollStoreConversationState | undefined =>
    scrollState.value.find(state => state.conversationId === conversationId);

  const getShowNewContentStateForConversationId = computed(() => {
    return (conversationId: string): ShowNewContentButtonState | undefined => {
      const conversationScrollState = getConversationById(conversationId);

      if (!conversationScrollState) {
        return;
      }

      return {
        showNewContentButton: conversationScrollState.showNewContentButton,
        newContentCount: conversationScrollState.newContentList.length,
      };
    };
  });

  const getScrollPositionAtTop = computed(() => {
    return (conversationId: string) => {
      const conversationScrollState = getConversationById(conversationId);

      if (!conversationScrollState) {
        return;
      }

      return conversationScrollState.scrollPositionAtTop;
    };
  });

  const getConversationScrollHeight = computed(() => {
    return (conversationId: string) => {
      const conversationScrollState = getConversationById(conversationId);

      if (!conversationScrollState?.messageContainer) {
        return 0;
      }

      return conversationScrollState.messageContainer.scrollHeight;
    };
  });

  const getConversationByIdOrThrow = (conversationId: string): ScrollStoreConversationState => {
    const conversationScrollState = getConversationById(conversationId);

    if (!conversationScrollState) {
      throw new Error(
        `Could not find conversation with id ${conversationId} in scroll store`,
      );
    }

    return conversationScrollState;
  };

  const onScroll = (conversationId: string): void => {
    const conversationScrollState = getConversationById(conversationId);
    if (isScrollPositionAtBottom(conversationId)) {
      hideNewContentButton(conversationId);
      if (conversationScrollState) {
        conversationScrollState.scrollPositionAtTop = false;
      }
    }
    else {
      if (conversationScrollState) {
        conversationScrollState.scrollPositionAtTop
          = isScrollPositionAtTop(conversationId);
      }

      showNewContentButton(conversationId);
    }
  };

  function setElementsForConversationId(
    conversationId: string,
    messageContainer: HTMLElement | null,
    bottomOfMessageContainer: HTMLElement | null,
  ): void {
    const conversationScrollState = getConversationById(conversationId);

    if (conversationScrollState) {
      conversationScrollState.messageContainer = messageContainer;
      conversationScrollState.bottomOfMessageContainer
        = bottomOfMessageContainer;
    }
  }

  function setNewContentButtonVisibility(
    conversationId: string,
    visible: boolean,
  ): void {
    const conversationScrollState = getConversationByIdOrThrow(conversationId);

    conversationScrollState.showNewContentButton = visible;

    if (visible === false) {
      conversationScrollState.newContentList = [];
    }
  }

  function showNewContentButton(conversationId: string): void {
    if (isScrollPositionAtBottom(conversationId)) {
      setNewContentButtonVisibility(conversationId, false);

      return;
    }

    setNewContentButtonVisibility(conversationId, true);
  }

  function hideNewContentButton(conversationId: string): void {
    setNewContentButtonVisibility(conversationId, false);
  }

  function isScrollPositionAtBottom(conversationId: string): boolean {
    const conversationScrollState = getConversationById(conversationId);

    if (!conversationScrollState?.messageContainer) {
      return false;
    }

    const scrollPosition = conversationScrollState.messageContainer.scrollTop;
    const scrollHeight = conversationScrollState.messageContainer.scrollHeight;
    const clientHeight = conversationScrollState.messageContainer.clientHeight;

    const TOLERANCE = 50;

    const calculatedScrollPosition = Math.abs(
      scrollHeight - (scrollPosition + clientHeight),
    );

    if (calculatedScrollPosition <= TOLERANCE) {
      return true;
    }

    if (conversationScrollState.newContentList.length > 0) {
      const maxScrollHeight = Math.max(
        ...conversationScrollState.newContentList.map(
          content => content.scrollHeight,
        ),
      );
      const newContentScrollDifference = scrollHeight - maxScrollHeight;

      // If the difference in scroll position before the new content was added and now is within tolerance, we can say that the user was at the bottom of the conversation
      if (Math.abs(newContentScrollDifference - calculatedScrollPosition) <= TOLERANCE) {
        return true;
      }
    }
    return false;
  }

  function isScrollPositionAtTop(conversationId: string): boolean {
    const conversationScrollState = getConversationByIdOrThrow(conversationId);

    if (!conversationScrollState.messageContainer) {
      return false;
    }

    const scrollTop = conversationScrollState.messageContainer.scrollTop;
    const TOP_TOLERANCE = 100;

    return scrollTop <= TOP_TOLERANCE;
  }

  async function scrollConversationToMessage(
    conversationId: string,
    targetMessage: HTMLElement,
    inputScrollOptions?: ScrollOptions,
  ): Promise<void> {
    const conversationScrollState = getConversationById(conversationId);
    const scrollOptions = { ...defaultScrollOptions, ...inputScrollOptions };

    if (!conversationScrollState) {
      return;
    }

    await nextTick();

    targetMessage.scrollIntoView(scrollOptions);
  }

  async function scrollConversationToBottom(
    conversationId: string,
    force = false,
    inputScrollOptions?: ScrollOptions,
  ): Promise<void> {
    const conversationScrollState = getConversationById(conversationId);
    const scrollOptions = { ...defaultScrollOptions, ...inputScrollOptions };

    if (!conversationScrollState) {
      return;
    }

    await nextTick();

    // If not at the bottom we don't just force scrolling to the bottom
    if (!isScrollPositionAtBottom(conversationId) && !force) {
      showNewContentButton(conversationId);

      return;
    }

    // If there is new content available, instead scroll to this new content
    if (conversationScrollState.newContentList.length > 0) {
      const newContentScrollPosition
        = conversationScrollState.newContentList[0];

      if (!newContentScrollPosition) {
        return;
      }

      conversationScrollState.messageContainer?.scrollTo({
        ...scrollOptions,
        top: newContentScrollPosition.scrollHeight,
      });

      if (isScrollPositionAtBottom(conversationId)) {
        hideNewContentButton(conversationId);
      }

      conversationScrollState.newContentList = [];

      return;
    }

    conversationScrollState.bottomOfMessageContainer?.scrollIntoView(
      scrollOptions,
    );

    conversationScrollState.showNewContentButton = false;
  }

  async function scrollConversationAfterLoadingMessages(
    conversationId: string,
    prevScrollHeight: number,
    inputScrollOptions?: ScrollIntoViewOptions,
  ): Promise<void> {
    const conversationScrollState = getConversationById(conversationId);
    const scrollOptions = { ...defaultScrollOptions, ...inputScrollOptions };

    if (!conversationScrollState) {
      return;
    }

    await nextTick();

    conversationScrollState.messageContainer?.scrollTo({
      ...scrollOptions,
      top:
        conversationScrollState.messageContainer.scrollHeight
        - prevScrollHeight,
    });
  }

  function prepareNewContent(conversationId: string): void {
    const conversationScrollState = getConversationById(conversationId);

    if (!conversationScrollState || !conversationScrollState.messageContainer) {
      return;
    }

    conversationScrollState.newContentList.push({
      scrollTop: conversationScrollState.messageContainer.scrollTop,
      clientHeight: conversationScrollState.messageContainer.clientHeight,
      scrollHeight: conversationScrollState.messageContainer.scrollHeight,
    });
  }

  function initializeScrollStateForConversations(
    openConversations: Array<{ conversationListResult: { id: string } }>,
  ): void {
    if (!openConversations) {
      return;
    }

    scrollState.value = openConversations.map((conversationState) => {
      const exists = scrollState.value.find(
        state =>
          state.conversationId === conversationState.conversationListResult.id,
      );

      if (exists) {
        return exists;
      }

      return {
        conversationId: conversationState.conversationListResult.id,
        showNewContentButton: false,
        messageContainer: null,
        bottomOfMessageContainer: null,
        newContentList: [],
      };
    });
  }

  return {
    scrollState,

    getShowNewContentStateForConversationId,
    getScrollPositionAtTop,
    getConversationScrollHeight,

    onScroll,

    isScrollPositionAtBottom,
    setElementsForConversationId,
    scrollConversationToBottom,
    scrollConversationAfterLoadingMessages,
    scrollConversationToMessage,
    prepareNewContent,
    initializeScrollStateForConversations,
  };
});
